<script lang="ts">
  import { offlineStatus, downloadAllContent } from '$stores/offline.js';

  // Progress ring geometry
  const RADIUS = 8;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  $: progress = $offlineStatus.phase === 'downloading' && $offlineStatus.total > 0
    ? $offlineStatus.current / $offlineStatus.total
    : 0;
  $: dashOffset = CIRCUMFERENCE * (1 - progress);

  $: label = $offlineStatus.phase === 'idle'
    ? __STRINGS__.offline.downloadButton
    : $offlineStatus.phase === 'downloading'
      ? __STRINGS__.offline.downloadProgress
          .replace('{current}', String($offlineStatus.current))
          .replace('{total}', String($offlineStatus.total))
      : $offlineStatus.phase === 'complete'
        ? __STRINGS__.offline.downloadComplete
        : __STRINGS__.offline.downloadFailed;
</script>

<div class="offline-download">
  {#if $offlineStatus.phase === 'idle'}
    <button
      class="offline-btn"
      on:click={downloadAllContent}
      aria-label={__STRINGS__.offline.downloadButton}
      title={__STRINGS__.offline.downloadButton}
    >
      <!-- Cloud download icon -->
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="8 17 12 21 16 17"/>
        <line x1="12" y1="21" x2="12" y2="9"/>
        <path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29"/>
      </svg>
    </button>

  {:else if $offlineStatus.phase === 'downloading'}
    <button
      class="offline-btn downloading"
      disabled
      aria-label={label}
      title={label}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
        <!-- Track circle -->
        <circle
          cx="12" cy="12" r={RADIUS}
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          opacity="0.25"
        />
        <!-- Progress arc — starts at top (rotate -90deg) -->
        <circle
          cx="12" cy="12" r={RADIUS}
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-dasharray={CIRCUMFERENCE}
          stroke-dashoffset={dashOffset}
          stroke-linecap="round"
          transform="rotate(-90 12 12)"
        />
      </svg>
      <span class="pct" aria-hidden="true">
        {$offlineStatus.total > 0 ? Math.round(progress * 100) : 0}%
      </span>
    </button>

  {:else if $offlineStatus.phase === 'complete'}
    <button
      class="offline-btn complete"
      disabled
      aria-label={__STRINGS__.offline.downloadComplete}
      title={__STRINGS__.offline.downloadComplete}
    >
      <!-- Checkmark -->
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    </button>

  {:else if $offlineStatus.phase === 'error'}
    <button
      class="offline-btn error"
      on:click={downloadAllContent}
      aria-label={__STRINGS__.offline.retryDownload}
      title="{__STRINGS__.offline.downloadFailed} — {__STRINGS__.offline.retryDownload}"
    >
      <!-- Warning triangle -->
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    </button>
  {/if}
</div>

<style>
  .offline-download {
    display: flex;
    align-items: center;
  }

  .offline-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: none;
    border: none;
    padding: 0.5rem;
    cursor: pointer;
    color: var(--color-menubar-text, #fff);
    opacity: 0.85;
    border-radius: 4px;
    transition: opacity 0.15s, background 0.15s;
    min-width: 32px;
    min-height: 32px;
  }

  .offline-btn:not(:disabled):hover {
    opacity: 1;
    background: var(--color-menubar-hover, rgba(255, 255, 255, 0.1));
  }

  .offline-btn:disabled {
    cursor: default;
  }

  .offline-btn.complete {
    opacity: 0.6;
  }

  .offline-btn.error {
    opacity: 1;
    color: #f4a261;
  }

  .offline-btn.error:hover {
    background: rgba(244, 162, 97, 0.15);
  }

  .pct {
    font-size: 0.6rem;
    font-weight: 700;
    line-height: 1;
    min-width: 20px;
    text-align: center;
  }
</style>
